import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatbot } from '../../contexts/ChatbotContext';
import { ChatbotSelector } from './ChatbotSelector';
import { NewChatbotButton } from './NewChatbotButton';
import { Search, X, Bot } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useChatbotStore } from '../../stores/chatbotStore';

// Type pour un chatbot
interface Chatbot {
  id: string;
  public_id: string;
  name: string;
  description?: string | null;
  created_at: string;
}

export function DashboardHeader() {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { fetchChatbotByPublicId } = useChatbotStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<Chatbot[]>([]);
  const [allChatbots, setAllChatbots] = useState<Chatbot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Récupérer tous les chatbots de l'utilisateur
  useEffect(() => {
    const fetchChatbots = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('chatbots')
          .select('id, public_id, name, description, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        setAllChatbots(data || []);
      } catch (err) {
        console.error('Error fetching chatbots:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchChatbots();
  }, [user, chatbotPublicId]);

  // Gérer la recherche
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    // Filtrer les chatbots par nom
    const results = allChatbots.filter(bot => 
      bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (bot.description && bot.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    setSearchResults(results);
    setShowResults(true);
  };

  // Effacer la recherche
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  // Sélectionner un chatbot dans les résultats
  const handleSelectChatbot = async (bot: Chatbot) => {
    try {
      // Fermer les résultats de recherche
      setShowResults(false);
      
      // Mettre à jour le chatbot actif dans le store
      await fetchChatbotByPublicId(bot.public_id);
      
      // Définir ce chatbot comme chatbot par défaut
      await supabase
        .from('chatbots')
        .update({ is_default: true })
        .eq('id', bot.id);
      
      // Réinitialiser les autres chatbots comme non-défaut
      if (user && user.id) {
        await supabase
          .from('chatbots')
          .update({ is_default: false })
          .neq('id', bot.id)
          .eq('user_id', user.id);
      }
      
      // Naviguer vers la page du chatbot
      navigate(`/app/dashboard`);
      
      // Effacer la recherche
      clearSearch();
    } catch (error) {
      console.error('Error selecting chatbot:', error);
    }
  };

  // Clic à l'extérieur pour fermer les résultats
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.search-container')) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="mb-8">
      {/* Desktop view */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-5">
        {/* Dashboard title and welcome message */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">
            Manage your chatbots and analyze their performance
          </p>
        </div>
        
        {/* Actions and tools */}
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {/* Search chatbots */}
          <div className="hidden md:block search-container relative">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search chatbots..."
                  className="bg-white/5 border border-white/10 rounded-lg pl-10 pr-9 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-60"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowResults(true)}
                />
                {searchQuery && (
                  <button
                    type="button" 
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                    onClick={clearSearch}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
            
            {/* Results dropdown */}
            {showResults && (
              <div className="absolute mt-2 w-full bg-gray-800/95 backdrop-blur-xl border border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="px-4 py-3 text-sm text-gray-400">
                    Loading chatbots...
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-1">
                    {searchResults.map((bot) => (
                      <button
                        key={bot.id}
                        className="block w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
                        onClick={() => handleSelectChatbot(bot)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                            <Bot className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-white font-medium">{bot.name}</div>
                            {bot.description && (
                              <div className="text-xs text-gray-400 truncate max-w-sm">
                                {bot.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-400">
                    No chatbots found matching "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chatbot selector */}
          <div className="order-2 md:order-1">
            {chatbotPublicId && <ChatbotSelector />}
          </div>
          
          {/* New chatbot button */}
          <div className="order-1 md:order-2">
            <NewChatbotButton />
          </div>
        </div>
      </div>
      
      {/* Mobile search */}
      <div className="md:hidden mt-4 search-container relative">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search chatbots..."
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-9 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery && setShowResults(true)}
            />
            {searchQuery && (
              <button
                type="button" 
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>
        
        {/* Mobile search results */}
        {showResults && (
          <div className="absolute mt-2 left-0 right-0 bg-gray-800/95 backdrop-blur-xl border border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-gray-400">
                Loading chatbots...
              </div>
            ) : searchResults.length > 0 ? (
              <div className="py-1">
                {searchResults.map((bot) => (
                  <button
                    key={bot.id}
                    className="block w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
                    onClick={() => handleSelectChatbot(bot)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                        <Bot className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{bot.name}</div>
                        {bot.description && (
                          <div className="text-xs text-gray-400 truncate max-w-[200px]">
                            {bot.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-400">
                No chatbots found matching "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}